import React, { memo, useContext, useEffect, useState } from 'react'
import { connect, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { Icon } from '../../common'
import { constants } from '../../../constants'
import { LanguageContext } from '../../../context/LanguageContext'
import { util } from '../../../util'

const BreadCrumb = ({
    paths,
    promoDetails,
    notificationDetails,
    storeDetails,
    orderDetails,
    productDetails,
}) => {
    const { formatString, language, strings } = useContext(LanguageContext)

    const [breadCrumbPaths, setBreadCrumbPaths] = useState({})
    const [pages, setPages] = useState(null)

    const user_permissions = useSelector(({ auth }) => auth.user_permissions)

    useEffect(() => {
        if (paths && paths.length > 1) {
            updateBreadCrumbPaths()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [promoDetails, paths, notificationDetails, storeDetails, productDetails])

    const updateBreadCrumbPaths = () => {
        const viewLinks = {}
        const pathLinks = paths
            .split('/')
            .map(page => page.replaceAll('-', '_'))
            .slice(1)

        pathLinks.forEach(link => {
            if (constants.routePaths.hasOwnProperty(link)) {
                viewLinks[link] = constants.routePaths[link]
            }
        })

        if (paths.includes('manage-promotions')) {
            viewLinks['manage_promotions'] = constants.routePaths.promotions
            if (promoDetails && promoDetails._id) {
                const promoPage = { ...constants.routePaths.promo_details }
                promoPage['path'] = promoPage.path.replace(':promoId', promoDetails._id)
                promoPage['value'] = promoDetails.name
                viewLinks[promoDetails._id] = promoPage
            }
        } else if (paths.includes('push-notifications')) {
            if (notificationDetails && notificationDetails._id) {
                const notificationPage = { ...constants.routePaths.push_notification_details }
                notificationPage['path'] = notificationPage.path.replace(
                    ':notificationId',
                    notificationDetails._id
                )

                notificationPage['value'] = (
                    (notificationDetails.localizations &&
                        notificationDetails.localizations[language]?.title) ||
                    notificationDetails.title
                ).slice(0, 65)
                viewLinks[notificationDetails._id] = notificationPage
            }
        } else if (paths.includes('stores')) {
            if (storeDetails && storeDetails.store_id) {
                const storePage = { ...constants.routePaths.store_details }
                storePage['path'] = storePage.path.replace(':storeId', storeDetails.store_id)
                storePage['value'] = formatString('BREADCRUMB_STORES_STORE_ID', {
                    storeId: storeDetails.store_id,
                })
                viewLinks[storeDetails.store_id] = storePage
            }
        } else if (paths.includes('orders')) {
            if (orderDetails && orderDetails.order_id) {
                const orderPage = { ...constants.routePaths.order_details }
                orderPage['path'] = orderPage.path.replace(':orderId', orderDetails.order_id)
                orderPage['value'] = formatString('BREADCRUMB_ORDERS_ORDER_ID', {
                    orderId: orderDetails.order_id,
                })
                viewLinks[orderDetails.order_id] = orderPage
            }
        } else if (paths.includes('add-new-products') || paths.includes('manage-updates')) {
            if (productDetails) {
                const details = paths.includes('add-new-products')
                    ? { ...constants.routePaths.product_details }
                    : { ...constants.routePaths.manage_update_product_details }

                const productPage = details
                productPage['path'] = productPage.path.replace(
                    ':productDraftId',
                    productDetails._id
                )
                productPage['value'] = productDetails.name
                viewLinks[productDetails._id] = productPage
            }
        } else if (paths.includes('products')) {
            if (
                productDetails &&
                productDetails.publish_info &&
                productDetails.publish_info.prod_published_product_id
            ) {
                const productPage = { ...constants.routePaths.products_product_details }
                productPage['path'] = productPage.path.replace(
                    ':productId',
                    productDetails.publish_info.prod_published_product_id
                )
                productPage['value'] = productDetails.name
                viewLinks[productDetails.publish_info.prod_published_product_id] = productPage
            }
        } else if (paths.includes('manage-store-import')) {
            viewLinks['manage_store_import'] = constants.routePaths.manage_stores_imports

            const splittedPath = paths.split('/')

            const store_id = splittedPath[3]
            viewLinks[store_id] = { value: store_id }

            const _id = splittedPath[4]
            viewLinks[_id] = { value: _id }
        } else if (paths.includes('manage-product-import')) {
            viewLinks['manage_product_import'] = constants.routePaths.manage_products_imports

            const splittedPath = paths.split('/')

            const product_id = splittedPath[3]
            viewLinks[product_id] = { value: product_id }

            const _id = splittedPath[4]
            viewLinks[_id] = { value: _id }
        } else if (paths.includes('manage-inventory-import')) {
            viewLinks['manage_inventory_import'] = constants.routePaths.manage_inventory_imports

            const splittedPath = paths.split('/')

            const store_id = splittedPath[3]
            viewLinks[store_id] = { value: store_id }

            const product_id = splittedPath[4]
            viewLinks[product_id] = { value: product_id }

            const _id = splittedPath[5]
            viewLinks[_id] = { value: _id }
        }

        setPages(pathLinks)
        setBreadCrumbPaths(viewLinks)
    }

    const breadCrumbValue = (breadCrumbPath, page) => {
        let value = breadCrumbPath.value
        if (value && value === constants.routePaths[page]?.value) {
            const string = `BREADCRUMB_${value.toUpperCase().replace(/[ ]/g, '_')}`
            value = strings[string]
        }
        return value
    }

    return (
        <div className={'bread-crumb'}>
            <div className={'bread-crumb__items'}>
                {pages &&
                    pages.map((page, i) => {
                        const breadCrumbPath = breadCrumbPaths[page]
                        if (!breadCrumbPath) return null

                        const value = breadCrumbValue(breadCrumbPath, page)

                        if (i === pages.length - 1)
                            return (
                                <div className={'bread-crumb__items--item'} key={page}>
                                    {value}
                                </div>
                            )

                        return (
                            <div className={'bread-crumb__items--item prev-page'} key={value}>
                                {breadCrumbPath.permission === undefined ||
                                user_permissions.includes(breadCrumbPath.permission) ? (
                                    <Link to={breadCrumbPath.path}>{value}</Link>
                                ) : (
                                    <span> {value} </span>
                                )}
                                <Icon
                                    size={'xxxs'}
                                    display={'inline'}
                                    className={'ml-3 mr-3'}
                                    renderIcon={util.ArrowForwardIosIcon}
                                />
                            </div>
                        )
                    })}
            </div>
        </div>
    )
}

const mapStateToProps = ({ promotions, pushNotifications, stores, products }) => ({
    promoDetails: promotions.promo_details,
    notificationDetails: pushNotifications.notification_details,
    storeDetails: stores.store_details,
    productDetails: products.get('product_details'),
})

export default connect(mapStateToProps)(memo(BreadCrumb))
